import { Button } from 'common-front/src/designSystem/components/button';
import {
    closeOtherDropdowns,
    Dropdown
} from 'common-front/src/designSystem/components/dropdown/dropdown';
import { ItemIcon } from 'common-front/src/designSystem/components/dropdown/item';
import { Menu } from 'common-front/src/designSystem/components/dropdown/menu';
import { Trigger } from 'common-front/src/designSystem/components/dropdown/trigger';
import { useHeavent } from 'common-front/src/hooks/useHeavent';
import { SegmentHeader } from 'common-front/src/segments/segmentHeader';
import { useSegmentsContext } from 'common-front/src/segments/segmentsContext';
import { useUserContext } from 'common-front/src/userContext';
import { ExportFormat } from 'common/src/generated/types';
import { HeaventPaths } from 'common/src/util/paths/heaventPaths';
import { getDocumentHref, getExportHref } from 'common/src/util/url';
import * as React from 'react';
import { useEventContext } from '../../events/show/eventContext';
import {
    useVolunteersExportMutation,
    useVolunteersHeaderQuery
} from '../../generated/graphqlHooks';
import { useNotificationContext } from '../../notifications/notificationContext';
import { AddExistingVolunteers } from '../create/addExistingVolunteers';
import { CreateNewVolunteer } from '../create/createNewVolunteer';
import { ApiExportModal } from '../export/apiExportModal';
import { FileExportModal } from '../export/fileExportModal';

interface IVolunteersHeaderProps {
    reload(): void;
}

export const VolunteersHeader = ({ reload }: IVolunteersHeaderProps) => {
    const {
        params: { organizationId, eventId, segmentId },
        translate
    } = useHeavent();
    const { user } = useUserContext();
    const { isEventAdmin, isPositionAdmin } = useEventContext();
    const { segment, columns } = useSegmentsContext();
    const { mutate: volunteersExport } = useVolunteersExportMutation();
    const { data, isLoading } = useVolunteersHeaderQuery({ eventId, segmentId });
    const [addExistingVolunteerOpen, setAddExistingVolunteerOpen] = React.useState(false);
    const [createNewVolunteerOpen, setCreateNewVolunteerOpen] = React.useState(false);
    const [isApiExportOpen, setApiExportOpen] = React.useState(false);
    const [isFileExportOpen, setFileExportOpen] = React.useState(false);
    const { checkExport } = useNotificationContext();
    const requestExport = async (format: ExportFormat, includeFiles: boolean) => {
        const { jobId } = await volunteersExport({
            eventId,
            segmentId: segment.id,
            format,
            includeFiles,
            columns
        });
        checkExport(organizationId, eventId, jobId);
    };

    return (
        <SegmentHeader>
            {isEventAdmin && (
                <Dropdown>
                    <Trigger>
                        <Button>{translate('ajouter_un_memb_33261')}</Button>
                    </Trigger>

                    <Menu placement="bottom-end">
                        <ItemIcon
                            icon="user-group"
                            onClick={(e) => {
                                closeOtherDropdowns(e.target);

                                setAddExistingVolunteerOpen(true);
                            }}
                        >
                            {translate('ajouter_des_mem_06922')}
                        </ItemIcon>

                        <ItemIcon
                            icon="user-plus"
                            onClick={(e) => {
                                closeOtherDropdowns(e.target);

                                setCreateNewVolunteerOpen(true);
                            }}
                        >
                            {translate('ajouter_un_nouv_29283')}
                        </ItemIcon>

                        <ItemIcon
                            icon="file-import"
                            to={HeaventPaths.EVENT_VOLUNTEERS_IMPORT(organizationId, eventId)}
                            onClick={(e) => closeOtherDropdowns(e.target)}
                        >
                            {translate('importer_via_un_61623')}
                        </ItemIcon>
                    </Menu>
                </Dropdown>
            )}

            <Dropdown>
                <Trigger>
                    <Button color="white" leftIcon="ellipsis-vertical" />
                </Trigger>

                <Menu placement="bottom-end">
                    {isPositionAdmin() && (
                        <>
                            <ItemIcon
                                icon="plug-circle-bolt"
                                onClick={(e) => {
                                    closeOtherDropdowns(e.target);

                                    setApiExportOpen(true);
                                }}
                            >
                                {translate('exporter_via_l_09843')}
                            </ItemIcon>
                            <ItemIcon
                                icon="file-xls"
                                onClick={(e) => {
                                    closeOtherDropdowns(e.target);

                                    setFileExportOpen(true);
                                }}
                            >
                                {translate('exporter_en_exc_51862')}
                            </ItemIcon>
                        </>
                    )}

                    <ItemIcon
                        download={`${segment.name}-users-listing`}
                        icon="arrow-down-to-bracket"
                        to={getDocumentHref(data.event?.segment.links.usersListingLink, 'pdf', {
                            lang: user?.language
                        })}
                    >
                        {translate('t_l_charger_en_59616')}
                    </ItemIcon>

                    <ItemIcon
                        icon="file-import"
                        to={HeaventPaths.EVENT_FILES_IMPORT(organizationId, eventId)}
                    >
                        {translate('importer_des_do_55420')}
                    </ItemIcon>
                </Menu>
            </Dropdown>

            {addExistingVolunteerOpen && (
                <AddExistingVolunteers
                    eventId={eventId}
                    organizationId={organizationId}
                    reload={reload}
                    onClose={() => {
                        setAddExistingVolunteerOpen(false);
                    }}
                />
            )}

            {createNewVolunteerOpen && (
                <CreateNewVolunteer
                    eventId={eventId}
                    organizationId={organizationId}
                    reload={reload}
                    showSendEmail={true}
                    subtitle={translate('cr_er_un_nouvea_00553')}
                    onClose={() => {
                        setCreateNewVolunteerOpen(false);
                    }}
                />
            )}

            {!isLoading && (
                <ApiExportModal
                    isOpen={isApiExportOpen}
                    link={getExportHref({
                        organizationId,
                        eventId,
                        segmentId,
                        key: data.event.key
                    })}
                    onClose={() => {
                        setApiExportOpen(false);
                    }}
                />
            )}
            <FileExportModal
                isOpen={isFileExportOpen}
                requestExport={requestExport}
                showIncludeFiles={true}
                onClose={() => {
                    setFileExportOpen(false);
                }}
            />
        </SegmentHeader>
    );
};
